<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ $t('SelfPerform.PlantDashboard.PlantFilterModal.title') }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="h-full w-full">
        <form
          action=""
          class="flex flex-col space-y-4"
        >
          <div class="flex space-x-4">
            <label class="w-1/2">
              {{ $t('SelfPerform.PlantDashboard.PlantFilterModal.label.assetId') }}
              <AppInput
                v-model="plantsStore.filter.form.fleet"
                type="text"
                name="fleet"
                data-test="filter-fleet"
                class="form-input"
              />
            </label>

            <label class="w-1/2">
              {{ $t('SelfPerform.PlantDashboard.PlantFilterModal.label.supplier') }}
              <AppInput
                v-model="plantsStore.filter.form.supplier_name"
                type="text"
                name="supplier_name"
                data-test="filter-supplier-name"
                class="form-input"
              />
            </label>
          </div>

          <label class="flex justify-center w-full">
            <div class="mx-auto">
              <button
                type="button"
                class="rounded-l-lg py-2 px-4 border-r border-black"
                :class="[plantsStore.filter.form.wet === true ? 'bg-gray-300' : 'bg-gray-100']"
                data-test="filter-type-wet"
                @click="plantsStore.filter.form.wet = true"
              >{{ $t('SelfPerform.PlantDashboard.PlantFilterModal.button.hour') }}</button>

              <button
                type="button"
                class="rounded-r-lg py-2 px-4"
                :class="[plantsStore.filter.form.wet === false ? 'bg-gray-300' : 'bg-gray-100']"
                data-test="filter-type-dry"
                @click="plantsStore.filter.form.wet = false"
              >{{ $t('SelfPerform.PlantDashboard.PlantFilterModal.button.day') }}</button>
            </div>
          </label>

          <div class="flex flex-col w-full">
            <label>
              {{ $t('SelfPerform.PlantDashboard.PlantFilterModal.label.recordDate') }}
            </label>

            <div class="flex w-full space-x-4 items-center justify-between">
              <div class="w-1/2">
                <AppInput
                  v-model="plantsStore.filter.form.day_min"
                  name="day_min"
                  type="date"
                  class="form-input"
                />
              </div>

              <div class="flex items-center space-x-2 text-gray-500">
                <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M17 8l4 4m0 0l-4 4m4-4H3"
    />
</svg>
              </div>

              <div class="w-1/2">
                <AppInput
                  v-model="plantsStore.filter.form.day_max"
                  name="day_max"
                  type="date"
                  class="form-input"
                />
              </div>
            </div>
          </div>

          <label>
            {{ $t('SelfPerform.PlantDashboard.PlantFilterModal.label.wbsCode') }}

            <AppSelect
              v-model.number="plantsStore.filter.form.wbs_code_id"
              :options="wbsCodes.map((code) => ({ value: code.id, label: code.code }))"
              name="wbs_code"
              data-test="wbs_code"
              class="form-input"
            />
          </label>

          <label>
            {{ $t('SelfPerform.PlantDashboard.PlantFilterModal.label.wbsDescription') }}

            <AppSelect
              v-model.number="plantsStore.filter.form.wbs_code_id"
              :options="wbsCodes.map((code) => ({ value: code.id, label: code.description }))"
              name="wbs_description"
              data-test="wbs_description"
              class="form-input"
            />
          </label>

          <label>
            {{ $t('SelfPerform.PlantDashboard.PlantFilterModal.label.vendorRef') }}

            <AppTypeAhead
              v-model="plantsStore.filter.form.vendor_ref"
              name="vendor_ref"
              :suggestion-callback="getSuggestions"
              data-test="filter-vendor-ref"
              class="form-input"
            />
          </label>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-end space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="$emit('clear')"
        >
          {{ $t('SelfPerform.PlantDashboard.PlantFilterModal.button.clear') }}
        </button>

        <button
          type="button"
          class="btn btn--primary-blue"
          data-test="apply-filter"
          @click="$emit('filter')"
        >
          {{ $t('SelfPerform.PlantDashboard.PlantFilterModal.button.applyFilter') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import Modal from '@/components/Modal.vue';
import { usePlantDailyRecordsStore } from '@/store/plantDailyRecord';
import { usePlantsStore } from '@/store/plants';
import { useProjectsStore } from '@/store/projects';
import { useWbsCodesStore } from '@/store/wbsCodes';
import { computed } from 'vue';

defineEmits<{
  (e: 'close');
  (e: 'filter');
  (e: 'clear');
}>();

const plantsStore = usePlantsStore();
const projectsStore = useProjectsStore();

const project = computed(() => {
  return projectsStore.project;
});

const plantDailyRecordsStore = usePlantDailyRecordsStore();
const wbsCodesStore = useWbsCodesStore();

const wbsCodes = computed(() => {
  return wbsCodesStore.models.where('project_id', project.value?.id).get();
});

const getSuggestions = () => {
  return plantDailyRecordsStore.search(project.value?.id, { vendor_ref: plantsStore.filter.form.vendor_ref });
};
</script>

<style scoped></style>
